"use client";

import { Fragment, useState } from "react";
import { matchPath, NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Dialog, Menu, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { AppRootState } from "../../redux/reducers";
import { Navigation, UserNavigation } from "../types";

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

const DefaultLayout = ({
  className,
  children,
  navigation = [],
  userNavigation = [],
}: {
  className?: string;
  children: JSX.Element;
  navigation?: Navigation;
  userNavigation?: UserNavigation;
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const userData: any = useSelector((state: AppRootState) => state.userData);

  const location = useLocation();
  const renderNavigation = () => (
    <ul className="-mx-2 space-y-1">
      {navigation.map((item) => {
        const pathMatches = matchPath(
          { path: item.href, caseSensitive: true, end: false },
          location.pathname
        );
        const isActive =
          !!pathMatches && item.href !== "/" && item.href !== "";

        return (
          <li key={item.name}>
            <NavLink
              className={classNames(
                isActive
                  ? "bg-brand-blue-400 text-white"
                  : "hover:bg-gray-100",
                "group flex gap-x-3 p-2 text-sm leading-6 font-semibold"
              )}
              to={item.href}
            >
              <item.icon className="h-6 w-6 shrink-0" aria-hidden="true" />
              {item.name}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );

  return (
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50 lg:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-900/80" />
            </Transition.Child>

            <div className="fixed inset-0 flex">
              <Transition.Child
                as={Fragment}
                enter="transition ease-in-out duration-300 transform"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transition ease-in-out duration-300 transform"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                  <Transition.Child
                    as={Fragment}
                    enter="ease-in-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in-out duration-300"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                      <button
                        type="button"
                        className="-m-2.5 p-2.5"
                        onClick={() => setSidebarOpen(false)}
                      >
                        <span className="sr-only">Close sidebar</span>
                        <XMarkIcon
                          className="h-6 w-6 text-white"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </Transition.Child>

                  {/* Sidebar for mobile */}
                  <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                    <div className="flex h-32 shrink-0 items-center pt-6 mb-6">
                      <img
                        className="h-28 w-auto"
                        src="../images/jack-logo.png"
                        alt=""
                      />
                    </div>
                    <nav className="flex flex-1 flex-col">
                      <ul className="flex flex-1 flex-col gap-y-7">
                        <li>
                          {renderNavigation()}
                        </li>
                      </ul>
                    </nav>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </Dialog>
        </Transition.Root>

        {/* Sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col">
          {/* Sidebar component, swap this element with another sidebar if you like */}
          <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4">
            <div className="flex h-32 shrink-0 items-center pt-6 mb-6">
              <img className="h-30 w-auto" src="../images/jack-logo.png" alt="" />
            </div>
            <nav className="flex flex-1 flex-col">
              <ul className="flex flex-1 flex-col gap-y-7">
                <li>
                  {renderNavigation()}
                </li>
              </ul>
            </nav>
          </div>
        </div>

        <div className="lg:pl-72">
          <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <button
              type="button"
              className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            {/* Separator */}
            <div
              className="h-6 w-px bg-gray-200 lg:hidden"
              aria-hidden="true"
            />

            <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <div className="relative flex flex-1 items-center">
                  <div className="header-content">
                    <div className="welcome-section">
                      <h1>Welcome back, {userData.given_name || 'User'}!</h1>
                      <p className="subtitle">Manage and view your patients' test results</p>
                    </div>
                  </div>
              </div>

              <div className="flex items-center gap-x-4 lg:gap-x-6">
                {/* Profile dropdown */}
                <Menu as="div" className="relative">
                  <Menu.Button className="-m-1.5 flex items-center p-1.5">
                    <span className="sr-only">Open user menu</span>
                    <img
                      className="h-8 w-8 rounded-full bg-gray-50"
                      src="../images/user.png"
                      alt=""
                    />
                    <span className="hidden lg:flex lg:items-center">
                      <span
                        className="ml-4 text-sm font-semibold leading-6 text-gray-900"
                        aria-hidden="true"
                      >
                        {userData.given_name} {userData.family_name}
                      </span>
                      <ChevronDownIcon
                        className="ml-2 h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    </span>
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2.5 w-48 origin-top-right bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none">
                      {userNavigation.map((item) => {
                        const pathMatches = matchPath(
                          { path: item.href, caseSensitive: true, end: false },
                          location.pathname
                        );
                        let isActive = false;

                        if (
                          pathMatches &&
                          item.href !== "/" &&
                          item.href !== ""
                        ) {
                          isActive = true;
                        }

                        return (
                          <Menu.Item key={item.name}>
                            {() => (
                              <NavLink
                                className={() =>
                                  isActive
                                    ? "block py-2 px-4 text-sm bg-brand-blue-400 text-white"
                                    : "block py-2 px-4 text-sm text-gray-700 hover:bg-gray-100"
                                }
                                to={item.href}
                              >
                                {item.name}
                              </NavLink>
                            )}
                          </Menu.Item>
                        );
                      })}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          <main className="py-2 bg-gray-100">
            <div className="px-4 sm:px-6 lg:px-8">{children}</div>
          </main>
        </div>
      </div>
  );
};

export default DefaultLayout;
